import { isDescendant, isRootId } from '@bsync/shared';
import { For, Show, createSignal } from 'solid-js';
import { edit, state } from '../lib/app';
import { ROOT_IDS, childFolders, countIn, titleOf } from '../lib/bookmarks';
import { dnd, draggedIds } from '../lib/dnd';
import { IconChevron, IconFolder } from './Icons';

const EXPANDED_KEY = 'bsync:expanded';

function loadExpanded(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(EXPANDED_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

const [expanded, setExpanded] = createSignal(loadExpanded());

export function expand(ids: string[]) {
  const next = new Set(expanded());
  ids.forEach((id) => next.add(id));
  setExpanded(next);
  try {
    localStorage.setItem(EXPANDED_KEY, JSON.stringify([...next]));
  } catch {
    /* storage unavailable */
  }
}

function toggle(id: string) {
  const next = new Set(expanded());
  if (next.has(id)) next.delete(id);
  else next.add(id);
  setExpanded(next);
  try {
    localStorage.setItem(EXPANDED_KEY, JSON.stringify([...next]));
  } catch {
    /* storage unavailable */
  }
}

/** True if the dragged items may be dropped into `target`. */
export function canDropInto(target: string, ids: string[] | null): boolean {
  const tree = state.tree();
  if (!tree || !ids?.length) return false;
  return ids.every((id) => id !== target && !isRootId(id) && (isRootId(target) || !isDescendant(tree, target, id)));
}

export function FolderTree(props: { current: string; onOpen: (id: string) => void }) {
  const roots = () => {
    const tree = state.tree();
    if (!tree) return [];
    // Hide empty browser-specific roots (menu, mobile) to reduce noise.
    return ROOT_IDS.filter((r) => r === 'root:toolbar' || r === 'root:other' || (tree.childrenOf.get(r)?.length ?? 0) > 0);
  };
  return (
    <nav class="tree" aria-label="Folders">
      <ul role="tree">
        <For each={roots()}>{(id) => <TreeItem id={id} depth={0} current={props.current} onOpen={props.onOpen} />}</For>
      </ul>
    </nav>
  );
}

function TreeItem(props: { id: string; depth: number; current: string; onOpen: (id: string) => void }) {
  const tree = () => state.tree()!;
  const kids = () => childFolders(tree(), props.id);
  const isOpen = () => expanded().has(props.id) || isRootId(props.id);
  const [over, setOver] = createSignal(false);

  const onDrop = async (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    const ids = draggedIds(e);
    if (!ids || !canDropInto(props.id, ids)) return;
    await edit((ed) => ed.move(ids, props.id));
    expand([props.id]);
  };

  return (
    <li role="treeitem" aria-expanded={kids().length ? isOpen() : undefined} aria-selected={props.current === props.id}>
      <div
        class="tree-row"
        classList={{ current: props.current === props.id, over: over(), root: isRootId(props.id) }}
        style={{ '--depth': props.depth }}
        onDragOver={(e) => {
          if (canDropInto(props.id, dnd.dragging())) {
            e.preventDefault();
            if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
            setOver(true);
          }
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
      >
        <Show when={kids().length && !isRootId(props.id)} fallback={<span class="chev-space" />}>
          <button class="tree-toggle" onClick={() => toggle(props.id)} aria-label={isOpen() ? 'Collapse' : 'Expand'}>
            <IconChevron open={isOpen()} />
          </button>
        </Show>
        <button class="tree-label" onClick={() => props.onOpen(props.id)} title={titleOf(tree(), props.id)}>
          <Show when={!isRootId(props.id)}>
            <IconFolder />
          </Show>
          <span class="ellipsis">{titleOf(tree(), props.id)}</span>
          <span class="count">{countIn(tree(), props.id) || ''}</span>
        </button>
      </div>
      <Show when={isOpen() && kids().length}>
        <ul role="group">
          <For each={kids()}>{(id) => <TreeItem id={id} depth={props.depth + 1} current={props.current} onOpen={props.onOpen} />}</For>
        </ul>
      </Show>
    </li>
  );
}
